import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';

import { useCombobox } from 'downshift';
import { Icon } from 'js/components';
import {
  COMBINED_LENS_NAME,
  lensPickerItemLabel,
} from './multi-assessment-radar-utils';

import orgPickerStyles from 'js/components/OrgNodePicker/OrgNodePicker.module.scss';
import styles from './CohortLensPicker.module.scss';

function itemToString(item) {
  return lensPickerItemLabel(item);
}

function matchesQuery(item, query) {
  if (!query) {
    return true;
  }
  const haystack = [item.name, item.breakoutName, lensPickerItemLabel(item)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function ToggleValue({ selectedItem }) {
  const showBreakoutPath = selectedItem?.kind === 'cohort';

  return (
    <div className={styles.toggleValue}>
      {showBreakoutPath ? (
        <div className={styles.viewing}>{selectedItem.breakoutName}</div>
      ) : null}
      <h2 className={orgPickerStyles['node-name']}>
        {selectedItem?.name ?? COMBINED_LENS_NAME}
      </h2>
    </div>
  );
}

function CohortLensPicker({
  items = [],
  selectedItem = null,
  onSelect = () => {},
  disabled = false,
  className,
}) {
  const searchInputRef = useRef(null);
  const isDisabled = disabled || items.length === 0;
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) => matchesQuery(item, query));
  }, [items, searchQuery]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredItems,
    selectedItem,
    itemToString,
    isItemDisabled: (item) => Boolean(item?.disabled),
    inputValue: searchQuery,
    onInputValueChange: ({ inputValue }) => {
      setSearchQuery(inputValue ?? '');
    },
    onSelectedItemChange: ({ selectedItem: next }) => {
      if (next && !next.disabled) {
        onSelect(next);
      }
    },
    onIsOpenChange: ({ isOpen: open }) => {
      if (open) {
        setSearchQuery('');
      }
    },
    stateReducer(_state, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.ToggleButtonClick:
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          return { ...changes, inputValue: '' };
        default:
          return changes;
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [isOpen]);

  const toggleButtonProps = getToggleButtonProps({
    disabled: isDisabled,
    'aria-label': 'Select cohort',
  });
  const searchInputProps = getInputProps({
    ref: searchInputRef,
    placeholder: 'Search cohorts…',
    'aria-label': 'Search cohorts',
  });
  const menuProps = getMenuProps();

  return (
    <div
      className={cx(orgPickerStyles.root, orgPickerStyles.compact, className)}
    >
      <div className={orgPickerStyles.control}>
        <button
          type="button"
          className={orgPickerStyles['toggle-button']}
          {...toggleButtonProps}
        >
          <ToggleValue selectedItem={selectedItem} />
          <Icon
            icon={['far', isOpen ? 'chevron-up' : 'chevron-down']}
            fixedWidth
            className="icon icon-right"
          />
        </button>
      </div>
      <div
        className={cx(orgPickerStyles.dropdown, {
          [orgPickerStyles['dropdown-open']]: isOpen,
        })}
      >
        <div className={orgPickerStyles.search}>
          <Icon
            icon={['far', 'search']}
            className={orgPickerStyles['search-icon']}
          />
          <input
            className={orgPickerStyles['search-input']}
            disabled={!isOpen || isDisabled}
            {...searchInputProps}
          />
        </div>
        <ul {...menuProps} className={orgPickerStyles.menu}>
          {!isOpen ? null : filteredItems.length === 0 ? (
            <li className={orgPickerStyles['empty-item']} key="no-results">
              {searchQuery.trim()
                ? 'No matching cohorts'
                : 'No cohorts available'}
            </li>
          ) : (
            filteredItems.map((item, index) => {
              const prev = index > 0 ? filteredItems[index - 1] : null;
              const showBreakoutSection =
                Boolean(item.breakoutName) &&
                item.breakoutName !== prev?.breakoutName;

              return (
                <Fragment key={item.id}>
                  {showBreakoutSection ? (
                    <li className={styles.sectionHeader} role="presentation">
                      {item.breakoutName}
                    </li>
                  ) : null}
                  <li
                    className={cx(
                      'combobox-item',
                      styles.item,
                      styles.cohortItem,
                      item.kind === 'combined' ? styles.combinedItem : null,
                      orgPickerStyles['search-item'],
                      {
                        highlighted:
                          highlightedIndex === index && !item.disabled,
                        selected: selectedItem?.id === item.id,
                        [styles.itemDisabled]: item.disabled,
                      }
                    )}
                    {...getItemProps({ item, index })}
                  >
                    <span className={styles.itemName}>{item.name}</span>
                  </li>
                </Fragment>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}

export default CohortLensPicker;
